"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.getSymmetricField = exports.LinkedRecordFieldLogic = void 0;
const zip_1 = __importDefault(require("lodash-es/zip"));
const clipboard_1 = require("../../clipboard");
const types_1 = require("@smartsuite/types");
const clipboard_2 = require("../../utils/clipboard");
const migration_1 = require("../../utils/migration");
const array_1 = require("../../helpers/array");
const intl_1 = require("../../locale/intl");
const linked_record_fieldtype_1 = require("../../safe-field-value/reference-fields/linked-record-field/linked-record.fieldtype");
const texts_1 = require("../../texts");
const record_1 = require("../../utils/record");
const reference_1 = require("../../utils/reference");
const base_reference_fieldtype_1 = require("../base-fields/base-reference.fieldtype");
const fields_1 = require("../../utils/fields");
const linked_record_1 = require("../../utils/linked-record");
const linked_record_2 = require("../../clipboard/linked-record");
class LinkedRecordFieldLogic extends base_reference_fieldtype_1.BaseReferenceFieldLogic {
    constructor(config, appRecordsService) {
        super(config, appRecordsService);
        this.name = "linkedrecordfield";
        this.label = (0, intl_1.$t)(texts_1.linkedRecordFieldText.linked_record);
        this.icon = "linked-records-2";
        this.iconColor = "#EC506E";
        this.placeholder = (0, intl_1.$t)(texts_1.linkedRecordFieldText.enter_record);
        this.searchSynonyms = [(0, intl_1.$t)(texts_1.linkedRecordFieldText.reference)];
        this.fieldValueGetter = new linked_record_fieldtype_1.LinkedRecordFieldValueGetter();
        this.recordModalExpandable = true;
        this.singleExportEligible = true;
        this.clipboardPasteRules = {
            linkedrecordfield: clipboard_1.clipboardLinkedRecordFieldToSelf,
            // Other supported copy/paste fields defined in constructor below
        };
        if (!appRecordsService) {
            return;
        }
        Object.keys(clipboard_2.textPasteRules).forEach((key) => {
            if (key === "linkedrecordfield") {
                // @ts-expect-error: Need to resolve
                this.clipboardPasteRules.linkedrecordfield = async (clipboardValue, srcField, targetField, userPreferences, currentValue) => {
                    var _a;
                    const isCopyWithTheSameLinkedApp = ((_a = srcField.params) === null || _a === void 0 ? void 0 : _a.linked_application) ===
                        targetField.params.linked_application;
                    const symmetricField = (0, exports.getSymmetricField)(targetField, appRecordsService);
                    const isPasteToFieldWithMultipleEntryParent = !(0, linked_record_1.isSingleEntryAllowedInParent)(targetField, symmetricField);
                    // It's a copy with the same linked application and multiple records allowed.
                    // We do not need to check linked relations, all values acceptable.
                    if (isCopyWithTheSameLinkedApp &&
                        isPasteToFieldWithMultipleEntryParent) {
                        return (0, clipboard_1.clipboardLinkedRecordFieldToSelf)(clipboardValue, srcField, targetField, userPreferences, currentValue);
                    }
                    // Not all values may be allowed.
                    // We need to fetch the linked records to make sure the links are acceptable.
                    return await (0, linked_record_2.clipboardTextToLinkedRecord)(appRecordsService)(clipboardValue, srcField, targetField, userPreferences, currentValue);
                };
            }
            else {
                // @ts-expect-error: Need to resolve
                this.clipboardPasteRules[key] =
                    (0, linked_record_2.clipboardTextToLinkedRecord)(appRecordsService);
            }
        });
    }
    getInlineEditRules(field) {
        const baseInlineEditRules = super.getInlineEditRules(field);
        return {
            ...baseInlineEditRules,
            editorWidth: "100%",
            switchToInputControl: "OnCellSelect",
            editOnStartTyping: false,
            saveKeys: types_1.navigationKeys,
            enableCopyEventInEditMode: true,
            enablePasteEventInEditMode: true,
            inputControlEditMode: "OnShow",
            cellPresenceEventMode: "OnModalOpen",
        };
    }
    isValueEqual(fieldValue1, fieldValue2) {
        return (0, zip_1.default)(fieldValue1, fieldValue2).every(([val1, val2]) => val1 === val2);
    }
    isFieldExpandableOnRecordModal(field) {
        return field.params.display_format !== "standard";
    }
    getNestedFields(field) {
        var _a, _b;
        const linkedApplication = this.appRecordsService.getApplication((_a = field.params.linked_application) !== null && _a !== void 0 ? _a : "");
        return (_b = linkedApplication === null || linkedApplication === void 0 ? void 0 : linkedApplication.structure) !== null && _b !== void 0 ? _b : [];
    }
    getNestedFormulaFieldsSchema(fieldSchema) {
        var _a, _b, _c;
        if (fieldSchema.linked_application) {
            const schema = (_b = (_a = this.appRecordsService).getFormulaApplicationSchema) === null || _b === void 0 ? void 0 : _b.call(_a, fieldSchema.linked_application);
            return (_c = schema === null || schema === void 0 ? void 0 : schema.fields) !== null && _c !== void 0 ? _c : [];
        }
        return [];
    }
    filterValueToString(filterValue, field, userPreferences, comparison) {
        if (["is_empty", "is_not_empty"].includes(comparison)) {
            return "";
        }
        if (["contains", "not_contains"].includes(comparison)) {
            return filterValue;
        }
        if (!Array.isArray(filterValue)) {
            return "";
        }
        return this.appRecordsService
            .getMultipleRelatedRecords((0, array_1.shallowUnique)(filterValue))
            .filter(record_1.isRecordNotDeleted)
            .map((item) => (0, record_1.getTitleOrUntitledRecordString)(item))
            .join(", ");
    }
    getFilterComponentData(field, comparison) {
        return {
            ...super.getFilterComponentData(field, comparison),
            placeholder: comparison === "contains" || comparison === "not_contains"
                ? (0, intl_1.$t)(texts_1.linkedRecordFieldText.enter_record)
                : (0, intl_1.$t)(texts_1.linkedRecordFieldText.select_record),
        };
    }
    getLinkedRecordFieldSmartWidth(_field) {
        return {
            defaultWidht: 170,
            minWidth: 90,
        };
    }
    migrateField(fieldTo, fieldFrom) {
        var _a;
        const migratedField = super.migrateField(fieldTo, fieldFrom);
        return (0, migration_1.migrateDisplayFormat)(migratedField, types_1.linkedRecordDisplayFormats, (_a = fieldTo.params.display_format) !== null && _a !== void 0 ? _a : "");
    }
    showSystemLockIcon(field, view, application, memberAccess) {
        var _a;
        if (view === "grid-cell") {
            const linkedApplication = this.appRecordsService.getApplication((_a = field.params.linked_application) !== null && _a !== void 0 ? _a : "");
            return !linkedApplication;
        }
        return super.showSystemLockIcon(field, view, application, memberAccess);
    }
    filterToRecordValue(filterValue, comparison, field) {
        const allowedComparisons = ["is_exactly", "has_all_of"];
        if (allowedComparisons.includes(comparison) &&
            (0, reference_1.isReferenceFieldValue)(filterValue)) {
            return filterValue;
        }
        throw new types_1.FilterToRecordValueConversionError(field, filterValue, comparison);
    }
    isSupportedByBulkAction(field) {
        const symmetricField = (0, exports.getSymmetricField)(field, this.appRecordsService);
        return !(0, linked_record_1.isSingleEntryAllowedInParent)(field, symmetricField);
    }
    canChangeValue(field, fromValue, toValue) {
        const symmetricField = (0, exports.getSymmetricField)(field, this.appRecordsService);
        if ((0, linked_record_1.isSingleEntryAllowedInParent)(field, symmetricField) &&
            !!(toValue === null || toValue === void 0 ? void 0 : toValue.length))
            return false;
        return super.canChangeValue(field, fromValue, toValue);
    }
    getRelatedRecordRequestsGetter(field) {
        if (field.params.linked_application) {
            return (fieldValue) => {
                return (fieldValue !== null && fieldValue !== void 0 ? fieldValue : []).map((recordId) => ({
                    recordId,
                    application_id: field.params.linked_application,
                }));
            };
        }
        return null;
    }
}
exports.LinkedRecordFieldLogic = LinkedRecordFieldLogic;
const getSymmetricField = (field, appRecordsService) => {
    if (!appRecordsService)
        return undefined;
    const linkedApplicationId = field.params.linked_application;
    if (!linkedApplicationId)
        return undefined;
    const symmetricField = field.params.symmetric_field;
    if (!symmetricField)
        return undefined;
    const linkedApplicationStructure = appRecordsService.getAppStructure(linkedApplicationId);
    if (!linkedApplicationStructure)
        return undefined;
    return (0, fields_1.getAppFieldBySlug)(linkedApplicationStructure, symmetricField);
};
exports.getSymmetricField = getSymmetricField;
